/**
 * Main entry point for Consenger Admin UI
 * Orchestrates all components and state management
 */

import { ConfigStore } from './state/config-store';
import { AuthStore } from './state/auth-store';
import { AuthAPI } from './services/auth-api';
import { TurnstileManager } from './services/turnstile';
import { APIClient } from './api';
import { TabManager } from './components/tabs/tab-manager';
import { ConfigSection } from './components/layout/config-section';
import { TokenDisplay } from './components/layout/token-display';
import { Legend } from './components/layout/legend';
import { GenericAuthForm, AUTH_FORM_CONFIGS } from './components/auth/generic-auth-form';
import { StreamingChatUI } from './streaming-chat';
import { SinglePromptUI } from './single-prompt';
import { TTSTestUI } from './tts-test';
import { TTSStreamingDebugUI } from './tts-streaming-debug';
import { ModelConfigUI } from './model-config-ui';
import { TaskRoutingUI } from './task-routing-ui';
import { AIRoutingUI } from './ai-routing-ui';
import { TierEnforcementUI } from './tier-enforcement-ui';
import { ServiceClientsUI } from './service-clients-ui';
import { RoutingChainVisualizerUI } from './routing-chain-visualizer';
import { GameGenerationUI } from './game-generation-ui';
import { LiveKitTestUI } from './livekit-test';
import type { BaseComponent } from './components/base-component';
import type { TabId } from './types';

class AdminUI {
  private configStore: ConfigStore;
  private authStore: AuthStore;
  private authAPI: AuthAPI;
  private apiClient: APIClient;
  private turnstileManager: TurnstileManager;
  private tabManager!: TabManager;
  private currentComponent: BaseComponent | null = null;
  private componentCache: Map<TabId, BaseComponent> = new Map(); // Cache components to preserve state

  constructor() {
    // Initialize stores (singletons)
    this.configStore = ConfigStore.getInstance();
    this.authStore = AuthStore.getInstance();

    // Load persisted state
    this.configStore.loadFromStorage();
    this.authStore.loadFromStorage();

    // Initialize services
    this.authAPI = new AuthAPI(this.configStore, this.authStore);
    this.turnstileManager = new TurnstileManager(this.configStore);

    // Refresh callback for APIClient
    const refreshCallback = async (): Promise<string | null> => {
      const refreshToken = this.authStore.getRefreshToken();
      if (!refreshToken) return null;

      try {
        const result = await this.authAPI.refreshToken(refreshToken);
        if (result.status === 200 && result.data.idToken) {
          this.authStore.setTokens(result.data);
          return result.data.idToken;
        }
      } catch (error) {
        console.error('Auto-refresh failed:', error);
      }
      return null;
    };

    this.apiClient = new APIClient(this.configStore.getConfig().apiBaseUrl, refreshCallback);
    // Initialize with stored token if available
    const initialToken = this.authStore.getIdToken();
    if (initialToken) {
      this.apiClient.setToken(initialToken);
    }

    // Subscribe to config changes to update APIClient
    this.configStore.subscribe((config) => {
      this.apiClient = new APIClient(config.apiBaseUrl, refreshCallback);
      const token = this.authStore.getIdToken();
      if (token) this.apiClient.setToken(token);
    });

    // Subscribe to auth changes to update APIClient token
    this.authStore.subscribe((tokens) => {
      this.apiClient.setToken(tokens?.idToken || null);
    });

    this.init();
  }

  private init(): void {
    // Render static layout components
    const configContainer = document.getElementById('config-container');
    if (configContainer) {
      new ConfigSection(configContainer, this.configStore).render();
    }

    const tokenDisplayContainer = document.getElementById('token-display-container');
    if (tokenDisplayContainer) {
      new TokenDisplay(tokenDisplayContainer, this.authStore, this.apiClient).render();
    }

    const legendContainer = document.getElementById('legend-container');
    if (legendContainer) {
      new Legend(legendContainer).render();
    }

    // Initialize tab manager
    const tabsContainer = document.getElementById('tabs-container');
    const contentContainer = document.getElementById('content-container');

    if (tabsContainer && contentContainer) {
      this.tabManager = new TabManager(tabsContainer, contentContainer, (tabId, container) =>
        this.handleTabChange(tabId, container)
      );
      this.tabManager.render();

      // Show default tab
      this.tabManager.showTab('signup');
    }

    // Handle upgrade success redirect
    this.handleUpgradeRedirect();
  }

  private handleTabChange(tabId: TabId, container: HTMLElement): void {
    // Don't destroy cached components - just hide them
    const cacheableTabs: TabId[] = ['tts', 'tts-streaming'];

    // Destroy current component only if it's not cacheable
    if (this.currentComponent) {
      const currentTab = this.tabManager.getCurrentTab();
      if (currentTab && !cacheableTabs.includes(currentTab)) {
        this.currentComponent.destroy();
      }
      this.currentComponent = null;
    }

    // Check cache first for stateful tabs
    if (cacheableTabs.includes(tabId) && this.componentCache.has(tabId)) {
      this.currentComponent = this.componentCache.get(tabId)!;
      this.currentComponent.render(); // Re-render to restore DOM
    } else {
      // Create new component
      this.currentComponent = this.createComponentForTab(tabId, container);

      // Cache stateful tabs
      if (cacheableTabs.includes(tabId)) {
        this.componentCache.set(tabId, this.currentComponent);
      }

      this.currentComponent.render();
    }
  }

  private createComponentForTab(tabId: TabId, container: HTMLElement): BaseComponent {
    // AI test tabs
    if (tabId === 'streaming-chat') {
      return new StreamingChatUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'single-prompt') {
      return new SinglePromptUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'tts') {
      return new TTSTestUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'tts-streaming') {
      return new TTSStreamingDebugUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'model-config') {
      return new ModelConfigUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'task-routing') {
      return new TaskRoutingUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'ai-routing') {
      return new AIRoutingUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'tier-enforcement') {
      return new TierEnforcementUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'service-clients') {
      return new ServiceClientsUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'routing-visualizer') {
      return new RoutingChainVisualizerUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'game-generation') {
      return new GameGenerationUI(container, this.apiClient) as any as BaseComponent;
    }
    if (tabId === 'livekit-test') {
      return new LiveKitTestUI(container, this.apiClient) as any as BaseComponent;
    }

    // Auth forms (generic)
    const config = AUTH_FORM_CONFIGS[tabId];
    if (config && config.title) {  // Check if it's a real config, not empty object
      return new GenericAuthForm(
        container,
        config,
        this.authAPI,
        this.authStore,
        this.turnstileManager
      );
    }

    // Fallback
    throw new Error(`Unknown tab: ${tabId}`);
  }

  private handleUpgradeRedirect(): void {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('upgrade') === 'success' && localStorage.getItem('pending-upgrade')) {
      localStorage.removeItem('pending-upgrade');
      this.autoRefreshToken();
    }
  }

  private async autoRefreshToken(): Promise<void> {
    const refreshToken = this.authStore.getRefreshToken();
    if (!refreshToken) return;

    try {
      const result = await this.authAPI.refreshToken(refreshToken);
      if (result.status === 200 && result.data.idToken) {
        this.authStore.setTokens(result.data);
        alert('✅ Upgrade successful! Your token has been refreshed with pro access.');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      alert('⚠️ Upgrade successful, but failed to refresh token. Please refresh manually.');
    }
  }
}

// Bootstrap
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new AdminUI());
} else {
  new AdminUI();
}
